import { Checkbox, Form, Input, Radio } from 'antd';
import { cn } from '@/libs/utils';
import {
  EMPLOYEE_WORK_TYPE_OPTIONS,
  WORK_TYPE_VALUES,
} from '@/shared/employee';
import { formValidateAndSetNumber } from '@/utils/inputApi';
import SpecialAbsenceSection from '../../SpecialAbsenceSection';
import { useEmployeeGeneralDataForm } from '../EmployeeGeneralDataFormContext';
import EmployeeWorkTimeProfilePanel from './EmployeeWorkTimeProfilePanel';

/** Work time card and the absence tracker of non-employment workers. */
export default function EmployeeWorkTimeFields() {
  const {
    COMPANY_DATA,
    form,
    isNonEmployment,
    isProfile,
    readOnly,
    sectionClass,
    shiftWorkerKeys,
    shiftWorkerState,
    t,
    workTimeCustomType,
    workTimeCustomTypeKeys,
    workTimeCustomTypeState,
    workTimeType,
    workTimeTypeKeys,
    workTimeTypeState,
  } = useEmployeeGeneralDataForm();

  return (
    <>
      {/* Work-time card (shift worker, full/half/custom time). Not applicable to
          special employment (no working-time fund) → hidden for those types. */}
      {/* On the profile: EmployeeWorkTimeProfilePanel. */}
      {!isNonEmployment && !isProfile && (
        <div
          className={cn(
            !isProfile && 'mb-5 rounded border bg-zinc-50 p-4',
            sectionClass('vreme')
          )}
        >
          <div className="grid gap-5">
            {COMPANY_DATA.applicationSettings?.weekendLeaves && (
              <Form.Item
                name={shiftWorkerKeys}
                label={null}
                valuePropName="checked"
                className="!mb-0"
              >
                <Checkbox disabled={shiftWorkerState?.disabled}>
                  <span>{t('shiftWorker', { ns: 'forms' })}</span>
                </Checkbox>
              </Form.Item>
            )}

            {workTimeTypeState?.visible && (
              <Form.Item
                name={workTimeTypeKeys}
                label={t('workType', { ns: 'forms' })}
                className="!mb-1"
              >
                <Radio.Group
                  className="custom-ant-radio-checkbox-absolute grid gap-4 sm:flex [&_label]:items-start [&_label]:justify-start"
                  disabled={workTimeTypeState?.disabled}
                >
                  {EMPLOYEE_WORK_TYPE_OPTIONS.map(({ key, value }) => {
                    return (
                      <Radio
                        key={key}
                        value={value}
                        className="mr-0 w-full rounded border border-zinc-200 bg-white px-2 py-1 hover:border-accent"
                      >
                        {t(key, {
                          ns: 'forms',
                          value:
                            value === 'customTime'
                              ? (workTimeCustomType ?? 0)
                              : (WORK_TYPE_VALUES[value] ?? 0),
                        })}
                      </Radio>
                    );
                  })}
                </Radio.Group>
              </Form.Item>
            )}

            {!WORK_TYPE_VALUES[workTimeType] &&
              workTimeCustomTypeState?.visible && (
                <Form.Item
                  name={workTimeCustomTypeKeys}
                  label={t('workPercentage', { ns: 'forms' })}
                  className="!mb-1"
                  rules={[
                    {
                      required: true,
                      message: t('formValidation.workPercentageRequired', {
                        ns: 'forms',
                      }),
                    },
                  ]}
                >
                  <Input
                    min={0}
                    max={100}
                    step="0.01"
                    type="number"
                    placeholder={t('workPercentage', { ns: 'forms' })}
                    prefix={<span className="mr-1 border-r pr-2.5">%</span>}
                    disabled={workTimeCustomTypeState?.disabled}
                    onChange={(e) =>
                      formValidateAndSetNumber(
                        form,
                        e,
                        workTimeCustomTypeKeys,
                        true
                      )
                    }
                  />
                </Form.Item>
              )}
          </div>
        </div>
      )}

      {!isNonEmployment && isProfile && (
        <div className={sectionClass('vreme')}>
          <EmployeeWorkTimeProfilePanel />
        </div>
      )}

      {/* Optional absence tracker — only for non-employment workers. NOT a
          legal annual-leave entitlement (no accrual / documents / timesheet). */}
      {isNonEmployment && (
        <div className={sectionClass('vreme')}>
          <SpecialAbsenceSection form={form} disabled={readOnly} />
        </div>
      )}
    </>
  );
}
